// Manual-trigger simulation hook. Wraps path-engine's
// /api/sessions/{id}/sendToDrone route and adapts the returned vk-sim
// trace into render-ready `FlightResponse[]` + `TelemetryPointResponse[]`
// stubs that plug into the existing useFlightPaths rate-bucket pipeline.
//
// No auto-fire. The ONLY trigger is the Flight Planner's Send to Drone
// button handler calling `triggerSimulation()`. Per Chris's 2026-04-15
// re-plan after c312ff8's auto-fire path shipped a polygon-clipping
// crash and had to be rolled back.
//
// FE → path-engine uses `useFetch.authenticatedPost` with the
// path-engine base URL injected as `apiHost`. Per Path Engine
// (board/2026-04-15_path-engine_sendtodrone-contract-answers.md), the
// route is currently `authenticated = false` but the JWT will be
// accepted-but-ignored, and Auth0 validation will drop in post-demo
// without an FE change. Path-engine → vk-sim auth (Bearer) is a
// server-side concern the FE never sees.
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import usePlannerFetch from 'hooks/usePlannerFetch';

import {
    SEND_TO_DRONE_PATH_PREFIX,
    SEND_TO_DRONE_PATH_SUFFIX,
    SIMULATION_FAILED_MESSAGE,
    SYNTHETIC_TRACE_EPOCH_MS
} from './constants';
import type {
    FlightResponse,
    MissionSummary,
    SimulationState,
    TelemetryPointResponse,
    TraceSample,
    UseFlightSimulationParams,
    UseFlightSimulationReturn,
    VkSimSimulateResponse
} from './types';

const IDLE_STATE: SimulationState = { status: 'idle' };

const RUNNING_STATE: SimulationState = { status: 'running' };

const EMPTY_TELEMETRY: Record<string, TelemetryPointResponse[]> = {};

const EMPTY_FLIGHTS: FlightResponse[] = [];

const toMissionSummary = ({ response }: { response: VkSimSimulateResponse }): MissionSummary => ({
    flightDurationSeconds: response.flightDurationSeconds,
    sampleCount: response.samples.length,
    tankUsedLitres: response.tankUsedL,
    waypointCount: response.waypointCount
});

// Build a synthetic TelemetryPointResponse from a single vk-sim trace
// sample. Field values fill the minimum surface area needed by
// telemetrySegmentUtil's buildRateSegments (isPumpOn,
// flowRateLitersPerMinute, swathWidthM, recordedAt,
// latitude/longitude/velocity) — every other field is stubbed to zero /
// empty string to satisfy the generated type.
//
// flowRateLitersPerMinute and isPumpOn are deliberately stubbed (not
// read from the sample) because vk-sim's reported flow + pump state
// are not yet trustworthy. Forcing flow=0 + pump=on means every
// derived `rateGalPerAc` is 0, which falls into the LOW bucket of
// useFlightPaths' rate-color step expression
// (0 ≤ rate < RATE_BUCKET_LOW_MAX_GAL_PER_AC = 2) → solid green for
// the full simulated path. Remove this stub once vk-sim's flow data
// is calibrated.
const traceSampleToTelemetryPoint = ({
    flightId,
    sample,
    swathWidthMeters
}: {
    flightId: string;
    sample: TraceSample;
    swathWidthMeters: number;
}): TelemetryPointResponse => ({
    altitude: sample.alt,
    drySpreadRateKgPerHa: 0,
    flightId,
    flowRateLitersPerMinute: 0,
    id: uuidv4(),
    isPumpOn: true,
    latitude: sample.lat,
    longitude: sample.lng,
    pitch: 0,
    recordedAt: new Date(SYNTHETIC_TRACE_EPOCH_MS + sample.tMs),
    roll: 0,
    swathWidthM: swathWidthMeters,
    turntableSpeedRpm: 0,
    velocityX: sample.speed,
    velocityY: 0,
    velocityZ: 0,
    yaw: sample.heading
});

// TODO(schema-mismatch): We fabricate a FlightResponse-shaped stub so the
// simulated trace plugs into `useFlightPaths`'s existing Mapbox layer
// pipeline without renderer changes. Only flightPath.coordinates,
// sprayPath.coordinates, swathWidthMeters and id are read downstream; every
// other field is zero / empty / stubbed. Resolve by exposing a first-class
// trace overlay prop on <Map>.
const buildSimulatedOutputs = ({
    swathWidthMeters,
    traceSamples
}: {
    swathWidthMeters: number;
    traceSamples: readonly TraceSample[];
}): {
    flights: FlightResponse[];
    telemetryPointsByFlightId: Record<string, TelemetryPointResponse[]>;
} => {
    if (traceSamples.length === 0) {
        return { flights: EMPTY_FLIGHTS, telemetryPointsByFlightId: EMPTY_TELEMETRY };
    }

    const flightId = uuidv4();
    const coordinates = traceSamples.map(sample => [sample.lng, sample.lat]);

    const synthetic = {
        flightPath: { coordinates },
        id: flightId,
        sprayPath: { coordinates: [] as number[][][] },
        swathWidthMeters
    };

    const telemetryPoints = traceSamples.map(sample =>
        traceSampleToTelemetryPoint({ flightId, sample, swathWidthMeters })
    );

    return {
        flights: [synthetic as unknown as FlightResponse],
        telemetryPointsByFlightId: { [flightId]: telemetryPoints }
    };
};

const useFlightSimulation = ({
    missionConfig,
    sessionId
}: UseFlightSimulationParams): UseFlightSimulationReturn => {
    const [simulationState, setSimulationState] = useState<SimulationState>(IDLE_STATE);

    // Reuse `usePlannerFetch` so every path-engine call the FE makes
    // shares one fetch surface — same base URL, same headers, same
    // auth posture. When Path Engine flips sendToDrone to authenticated
    // post-demo, `usePlannerFetch` itself switches to `authenticatedPost`
    // and this hook picks up the change for free.
    const { plannerPost } = usePlannerFetch();

    const triggerSimulation = useCallback((): void => {
        if (sessionId === null) {
            return;
        }

        setSimulationState(RUNNING_STATE);

        const url = `${SEND_TO_DRONE_PATH_PREFIX}/${sessionId}${SEND_TO_DRONE_PATH_SUFFIX}`;

        void (async () => {
            const { data, error } = await plannerPost<VkSimSimulateResponse>(url, undefined);

            if (error !== null || data === null) {
                setSimulationState({
                    error: error ?? SIMULATION_FAILED_MESSAGE,
                    status: 'error'
                });

                return;
            }

            setSimulationState({
                status: 'complete',
                summary: toMissionSummary({ response: data }),
                traceSamples: data.samples
            });
        })();
    }, [plannerPost, sessionId]);

    const clearSimulation = useCallback((): void => {
        setSimulationState(IDLE_STATE);
    }, []);

    const simulatedOutputs = useMemo(() => {
        if (simulationState.status !== 'complete') {
            return { flights: EMPTY_FLIGHTS, telemetryPointsByFlightId: EMPTY_TELEMETRY };
        }

        return buildSimulatedOutputs({
            swathWidthMeters: missionConfig.swathWidthM,
            traceSamples: simulationState.traceSamples
        });
    }, [missionConfig.swathWidthM, simulationState]);

    const isSimulating = simulationState.status === 'running';
    const simulationError = simulationState.status === 'error' ? simulationState.error : null;

    return {
        clearSimulation,
        isSimulating,
        simulatedFlights: simulatedOutputs.flights,
        simulatedTelemetryPointsByFlightId: simulatedOutputs.telemetryPointsByFlightId,
        simulationError,
        simulationState,
        triggerSimulation
    };
};

export default useFlightSimulation;
